import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    adminLogin,
    setAdminToken,
    adminCreatePost,
    adminUpdatePost,
    adminDeletePost,
    adminBumpPost,
    adminCreateEvent,
    adminUpdateEvent,
    adminDeleteEvent,
    adminUpdateSettings,
    adminGetWebhookUrl,
    adminListSubmissions,
    adminGetSpamLog,
    getPosts,
    getEvents,
    getSettings,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LOGO_URL, POST_CATEGORIES } from "@/lib/constants";
import PostExpiryBadge from "@/components/PostExpiryBadge";
import SubmissionsPanel from "@/components/admin/SubmissionsPanel";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, LogOut, Video, Zap, Copy } from "lucide-react";

const EMPTY_POST = {
    slug: "",
    title: "",
    sub_header: "",
    blurb: "",
    body: "",
    category: "OPENING",
    type: "latest",
    image_url: "",
    thumbnails: "",
    contact_name: "",
    business: "",
    phone: "",
    website: "",
    date: "",
};

const EMPTY_EVENT = {
    date: "",
    title: "",
    description: "",
    location: "",
    time: "",
    link: "",
};

function PostForm({ initial, onSubmit, onCancel }) {
    const [form, setForm] = useState({
        ...EMPTY_POST,
        ...initial,
        thumbnails: Array.isArray(initial?.thumbnails)
            ? initial.thumbnails.join("\n")
            : initial?.thumbnails || "",
    });

    const set = (k) => (e) =>
        setForm((f) => ({
            ...f,
            [k]: e.target ? e.target.value : e,
        }));

    const submit = (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            thumbnails: form.thumbnails
                ? form.thumbnails
                      .split("\n")
                      .map((s) => s.trim())
                      .filter(Boolean)
                : [],
        };
        onSubmit(payload);
    };

    return (
        <form
            onSubmit={submit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="post-form"
        >
            <div>
                <Label>Slug *</Label>
                <Input
                    value={form.slug}
                    onChange={set("slug")}
                    required
                    data-testid="post-slug-input"
                />
            </div>
            <div>
                <Label>Date *</Label>
                <Input
                    type="date"
                    value={form.date}
                    onChange={set("date")}
                    required
                    data-testid="post-date-input"
                />
            </div>
            <div className="md:col-span-2">
                <Label>Title *</Label>
                <Input
                    value={form.title}
                    onChange={set("title")}
                    required
                    data-testid="post-title-input"
                />
            </div>
            <div className="md:col-span-2">
                <Label>Sub-header</Label>
                <Input value={form.sub_header} onChange={set("sub_header")} />
            </div>
            <div className="md:col-span-2">
                <Label>Blurb (≤100 chars)</Label>
                <Input
                    value={form.blurb}
                    onChange={set("blurb")}
                    maxLength={120}
                    data-testid="post-blurb-input"
                />
            </div>
            <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={set("category")}>
                    <SelectTrigger data-testid="post-category-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                        {POST_CATEGORIES.map((c) => (
                            <SelectItem key={c} value={c}>
                                {c}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={set("type")}>
                    <SelectTrigger data-testid="post-type-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="featured">Featured</SelectItem>
                        <SelectItem value="recent">Recent</SelectItem>
                        <SelectItem value="latest">Latest</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="md:col-span-2">
                <Label>Image URL *</Label>
                <Input
                    value={form.image_url}
                    onChange={set("image_url")}
                    required
                    data-testid="post-image-input"
                />
            </div>
            <div className="md:col-span-2">
                <Label>Thumbnails (one URL per line)</Label>
                <Textarea
                    value={form.thumbnails}
                    onChange={set("thumbnails")}
                    rows={3}
                />
            </div>
            <div>
                <Label>Contact Name</Label>
                <Input
                    value={form.contact_name}
                    onChange={set("contact_name")}
                />
            </div>
            <div>
                <Label>Business</Label>
                <Input value={form.business} onChange={set("business")} />
            </div>
            <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={set("phone")} />
            </div>
            <div>
                <Label>Website</Label>
                <Input value={form.website} onChange={set("website")} />
            </div>
            <div className="md:col-span-2">
                <Label>Body</Label>
                <Textarea
                    value={form.body}
                    onChange={set("body")}
                    rows={8}
                    data-testid="post-body-input"
                />
            </div>
            <div className="md:col-span-2 flex gap-3">
                <Button
                    type="submit"
                    className="bg-[#E63B26] hover:bg-[#1B1B1B] text-white rounded-none"
                    data-testid="post-form-submit"
                >
                    Save Post
                </Button>
                {onCancel && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        className="rounded-none"
                    >
                        Cancel
                    </Button>
                )}
            </div>
        </form>
    );
}

function EventForm({ initial, onSubmit, onCancel }) {
    const [form, setForm] = useState({ ...EMPTY_EVENT, ...initial });
    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));
    const submit = (e) => {
        e.preventDefault();
        onSubmit(form);
    };
    return (
        <form
            onSubmit={submit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="event-form"
        >
            <div>
                <Label>Date * (YYYY-MM-DD)</Label>
                <Input
                    type="date"
                    value={form.date}
                    onChange={set("date")}
                    required
                    data-testid="event-date-input"
                />
            </div>
            <div>
                <Label>Time</Label>
                <Input value={form.time} onChange={set("time")} placeholder="7:00 PM" />
            </div>
            <div className="md:col-span-2">
                <Label>Title *</Label>
                <Input
                    value={form.title}
                    onChange={set("title")}
                    required
                    data-testid="event-title-input"
                />
            </div>
            <div className="md:col-span-2">
                <Label>Location</Label>
                <Input value={form.location} onChange={set("location")} />
            </div>
            <div className="md:col-span-2">
                <Label>Link</Label>
                <Input value={form.link} onChange={set("link")} />
            </div>
            <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea
                    value={form.description}
                    onChange={set("description")}
                    rows={4}
                />
            </div>
            <div className="md:col-span-2 flex gap-3">
                <Button
                    type="submit"
                    className="bg-[#E63B26] hover:bg-[#1B1B1B] text-white rounded-none"
                    data-testid="event-form-submit"
                >
                    Save Event
                </Button>
                {onCancel && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        className="rounded-none"
                    >
                        Cancel
                    </Button>
                )}
            </div>
        </form>
    );
}

export default function AdminPage() {
    const [authed, setAuthed] = useState(
        !!localStorage.getItem("benne_admin_token"),
    );
    const [password, setPassword] = useState("");

    const [posts, setPosts] = useState([]);
    const [events, setEvents] = useState([]);
    const [editingPost, setEditingPost] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);
    const [showPostForm, setShowPostForm] = useState(false);
    const [showEventForm, setShowEventForm] = useState(false);
    const [settings, setSettings] = useState({
        hero_video_url: "",
        hero_video_title: "",
        hero_video_caption: "",
        countdown_enabled: false,
    });
    const [webhookInfo, setWebhookInfo] = useState(null);
    const [subCounts, setSubCounts] = useState({ queue: 0, banned: 0 });
    const [spamLog, setSpamLog] = useState([]);

    const reload = useCallback(async () => {
        try {
            const [p, e, s, wh, subs, sl] = await Promise.all([
                getPosts(),
                getEvents(),
                getSettings(),
                adminGetWebhookUrl().catch(() => null),
                adminListSubmissions().catch(() => []),
                adminGetSpamLog().catch(() => []),
            ]);
            setPosts(p);
            setEvents(e);
            setSettings({
                hero_video_url: s.hero_video_url || "",
                hero_video_title: s.hero_video_title || "",
                hero_video_caption: s.hero_video_caption || "",
                countdown_enabled: !!s.countdown_enabled,
            });
            setWebhookInfo(wh);
            const banned = subs.filter((x) => x.status === "banned").length;
            setSubCounts({ queue: subs.length - banned, banned });
            setSpamLog(sl);
        } catch (err) {
            console.error("admin reload failed:", err);
        }
    }, []);

    useEffect(() => {
        if (authed) reload();
    }, [authed, reload]);

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const res = await adminLogin(password);
            setAdminToken(res.token);
            setAuthed(true);
            toast.success("Welcome to the BENNE admin.");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                const detail =
                    err?.response?.data?.detail ||
                    "Too many attempts. Try again later.";
                toast.error(detail, {
                    description: "Brute-force protection engaged.",
                });
            } else {
                toast.error("Wrong password.");
            }
        }
    };

    const logout = () => {
        setAdminToken(null);
        setAuthed(false);
    };

    const savePost = async (payload) => {
        try {
            if (editingPost?.id) {
                await adminUpdatePost(editingPost.id, payload);
                toast.success("Post updated.");
            } else {
                await adminCreatePost(payload);
                toast.success("Post created.");
            }
            setEditingPost(null);
            setShowPostForm(false);
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        }
    };

    const removePost = async (id) => {
        if (!confirm("Delete this post?")) return;
        await adminDeletePost(id);
        reload();
    };

    const bumpPost = async (id) => {
        try {
            await adminBumpPost(id);
            toast.success("Bumped — another 72 h on the home page.");
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bump failed");
        }
    };

    const saveEvent = async (payload) => {
        try {
            if (editingEvent?.id) {
                await adminUpdateEvent(editingEvent.id, payload);
                toast.success("Event updated.");
            } else {
                await adminCreateEvent(payload);
                toast.success("Event created.");
            }
            setEditingEvent(null);
            setShowEventForm(false);
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        }
    };

    const removeEvent = async (id) => {
        if (!confirm("Delete this event?")) return;
        await adminDeleteEvent(id);
        reload();
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        try {
            await adminUpdateSettings(settings);
            toast.success("Settings saved.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save settings");
        }
    };

    if (!authed) {
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-[#1B1B1B] p-6"
                data-testid="admin-login-screen"
            >
                <form
                    onSubmit={handleLogin}
                    className="bg-white p-8 max-w-md w-full"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <img
                            src={LOGO_URL}
                            alt="BENNE"
                            className="h-12 w-12"
                        />
                        <h1 className="font-poppins font-black text-2xl">
                            BENNE Admin
                        </h1>
                    </div>
                    <Label>Password</Label>
                    <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="admin-password-input"
                        className="mb-4"
                    />
                    <Button
                        type="submit"
                        className="w-full bg-[#E63B26] hover:bg-[#1B1B1B] text-white rounded-none h-12 font-poppins tracking-[0.18em] uppercase"
                        data-testid="admin-login-submit"
                    >
                        Sign In
                    </Button>
                    <Link
                        to="/"
                        className="block mt-4 text-center font-sofia text-sm text-[#1B1B1B]/60 hover:text-[#E63B26]"
                    >
                        ← Back to site
                    </Link>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDF9EF]" data-testid="admin-dashboard">
            <header className="bg-[#1B1B1B] text-white px-6 py-4 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3">
                    <img src={LOGO_URL} alt="BENNE" className="h-10 w-10" />
                    <span className="font-poppins font-black text-xl">
                        BENNE Admin
                    </span>
                </Link>
                <Button
                    variant="ghost"
                    onClick={logout}
                    className="text-white hover:text-[#F5C53D] hover:bg-transparent"
                    data-testid="admin-logout-button"
                >
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                </Button>
            </header>

            <main className="max-w-6xl mx-auto p-6">
                <Tabs defaultValue="submissions">
                    <TabsList className="mb-6">
                        <TabsTrigger value="submissions" data-testid="tab-submissions">
                            Submissions ({subCounts.queue})
                        </TabsTrigger>
                        <TabsTrigger value="posts" data-testid="tab-posts">
                            Posts ({posts.length})
                        </TabsTrigger>
                        <TabsTrigger value="events" data-testid="tab-events">
                            Events ({events.length})
                        </TabsTrigger>
                        <TabsTrigger value="blacklist" data-testid="tab-blacklist">
                            Blacklist ({subCounts.banned})
                        </TabsTrigger>
                        <TabsTrigger value="settings" data-testid="tab-settings">
                            Settings
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="submissions">
                        <SubmissionsPanel mode="queue" />
                    </TabsContent>

                    <TabsContent value="blacklist">
                        <SubmissionsPanel mode="blacklist" />
                    </TabsContent>

                    <TabsContent value="posts">
                        {!showPostForm && (
                            <Button
                                onClick={() => {
                                    setEditingPost(null);
                                    setShowPostForm(true);
                                }}
                                className="mb-4 bg-[#E63B26] hover:bg-[#1B1B1B] text-white rounded-none"
                                data-testid="add-post-button"
                            >
                                <Plus className="h-4 w-4 mr-2" /> New Post
                            </Button>
                        )}
                        {showPostForm && (
                            <div className="bg-white p-6 mb-6 border">
                                <PostForm
                                    initial={editingPost || {}}
                                    onSubmit={savePost}
                                    onCancel={() => {
                                        setShowPostForm(false);
                                        setEditingPost(null);
                                    }}
                                />
                            </div>
                        )}
                        <div className="bg-white border">
                            {posts.map((p) => (
                                <div
                                    key={p.id}
                                    className="flex items-center gap-4 p-3 border-b last:border-b-0"
                                    data-testid={`admin-post-row-${p.slug}`}
                                >
                                    <img
                                        src={p.image_url}
                                        alt=""
                                        className="w-14 h-14 object-cover"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-poppins font-semibold truncate">
                                            {p.title}
                                        </p>
                                        <p className="text-xs text-[#1B1B1B]/60 uppercase tracking-wider">
                                            {p.type} · {p.category} · {p.date}
                                        </p>
                                        <div className="mt-1.5">
                                            <PostExpiryBadge post={p} />
                                        </div>
                                    </div>
                                    {(p.type === "featured" || p.type === "recent") && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => bumpPost(p.id)}
                                            className="border-[#F5C53D] text-[#1B1B1B] hover:bg-[#F5C53D]"
                                            title="Reset 72h timer"
                                            data-testid={`bump-post-${p.slug}`}
                                        >
                                            <Zap className="h-3 w-3 mr-1" />
                                            Bump 72h
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            setEditingPost(p);
                                            setShowPostForm(true);
                                        }}
                                        data-testid={`edit-post-${p.slug}`}
                                    >
                                        <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => removePost(p.id)}
                                        data-testid={`delete-post-${p.slug}`}
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="events">
                        {!showEventForm && (
                            <Button
                                onClick={() => {
                                    setEditingEvent(null);
                                    setShowEventForm(true);
                                }}
                                className="mb-4 bg-[#E63B26] hover:bg-[#1B1B1B] text-white rounded-none"
                                data-testid="add-event-button"
                            >
                                <Plus className="h-4 w-4 mr-2" /> New Event
                            </Button>
                        )}
                        {showEventForm && (
                            <div className="bg-white p-6 mb-6 border">
                                <EventForm
                                    initial={editingEvent || {}}
                                    onSubmit={saveEvent}
                                    onCancel={() => {
                                        setShowEventForm(false);
                                        setEditingEvent(null);
                                    }}
                                />
                            </div>
                        )}
                        <div className="bg-white border">
                            {events.map((e) => (
                                <div
                                    key={e.id}
                                    className="flex items-center gap-4 p-3 border-b last:border-b-0"
                                    data-testid={`admin-event-row-${e.id}`}
                                >
                                    <div className="bg-[#F5C53D] text-[#1B1B1B] px-3 py-2 font-poppins font-bold text-sm min-w-[90px] text-center">
                                        {e.date}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-poppins font-semibold truncate">
                                            {e.title}
                                        </p>
                                        <p className="text-xs text-[#1B1B1B]/60">
                                            {e.time} · {e.location}
                                        </p>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            setEditingEvent(e);
                                            setShowEventForm(true);
                                        }}
                                        data-testid={`edit-event-${e.id}`}
                                    >
                                        <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => removeEvent(e.id)}
                                        data-testid={`delete-event-${e.id}`}
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="settings">
                        <form
                            onSubmit={saveSettings}
                            className="bg-white border p-6 max-w-2xl space-y-5"
                            data-testid="settings-form"
                        >
                            <div className="flex items-center gap-3">
                                <div className="bg-[#1B1B1B] text-[#F5C53D] p-2.5">
                                    <Video className="h-5 w-5" />
                                </div>
                                <div>
                                    <h2 className="font-poppins font-bold text-xl">
                                        Homepage Video Embed
                                    </h2>
                                    <p className="font-sofia text-sm text-[#1B1B1B]/70">
                                        Paste a YouTube, Vimeo or direct MP4
                                        URL. The block renders 3:2 on the home
                                        page beneath Latest Posts.
                                    </p>
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="hv-url">Video URL</Label>
                                <Input
                                    id="hv-url"
                                    value={settings.hero_video_url}
                                    onChange={(e) =>
                                        setSettings((s) => ({
                                            ...s,
                                            hero_video_url: e.target.value,
                                        }))
                                    }
                                    placeholder="https://www.youtube.com/watch?v=… or https://cdn.example.com/clip.mp4"
                                    data-testid="settings-video-url"
                                />
                            </div>
                            <div>
                                <Label htmlFor="hv-title">Title (optional)</Label>
                                <Input
                                    id="hv-title"
                                    value={settings.hero_video_title}
                                    onChange={(e) =>
                                        setSettings((s) => ({
                                            ...s,
                                            hero_video_title: e.target.value,
                                        }))
                                    }
                                    data-testid="settings-video-title"
                                />
                            </div>
                            <div>
                                <Label htmlFor="hv-caption">Caption (optional)</Label>
                                <Textarea
                                    id="hv-caption"
                                    rows={3}
                                    value={settings.hero_video_caption}
                                    onChange={(e) =>
                                        setSettings((s) => ({
                                            ...s,
                                            hero_video_caption: e.target.value,
                                        }))
                                    }
                                    data-testid="settings-video-caption"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="bg-[#E63B26] hover:bg-[#1B1B1B] text-white rounded-none"
                                data-testid="settings-save"
                            >
                                Save Settings
                            </Button>
                        </form>

                        <div
                            className="bg-white border p-6 max-w-2xl mt-6"
                            data-testid="countdown-toggle-card"
                        >
                            <div className="flex items-start gap-4">
                                <div className="bg-[#1B1B1B] text-[#F5C53D] p-3">
                                    <Zap className="h-5 w-5" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-poppins font-bold text-lg">
                                        Home-page 72h countdown
                                    </h3>
                                    <p className="font-sofia text-sm text-[#1B1B1B]/70 mt-1">
                                        When <strong>OFF</strong>: every Featured
                                        and Most Recent post stays on the home
                                        page indefinitely — useful while seeding
                                        the site for funders / launch reviewers.
                                        When <strong>ON</strong>: Featured /
                                        Most Recent posts roll off the home
                                        page after 72 h and live in the
                                        Categories archive.
                                    </p>
                                    <p className="font-sofia text-xs text-[#1B1B1B]/60 mt-2">
                                        Current state:{" "}
                                        <strong
                                            className={
                                                settings.countdown_enabled
                                                    ? "text-[#E63B26]"
                                                    : "text-[#54547A]"
                                            }
                                        >
                                            {settings.countdown_enabled
                                                ? "ON — countdown active"
                                                : "OFF — mockups frozen"}
                                        </strong>
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    onClick={async () => {
                                        const next = !settings.countdown_enabled;
                                        try {
                                            await adminUpdateSettings({
                                                countdown_enabled: next,
                                            });
                                            setSettings((s) => ({
                                                ...s,
                                                countdown_enabled: next,
                                            }));
                                            toast.success(
                                                next
                                                    ? "Countdown ON — Featured/Most Recent expire after 72 h."
                                                    : "Countdown OFF — mockups will stay visible.",
                                            );
                                        } catch (err) {
                                            toast.error(
                                                err?.response?.data?.detail ||
                                                    "Toggle failed",
                                            );
                                        }
                                    }}
                                    className={
                                        settings.countdown_enabled
                                            ? "bg-[#E63B26] hover:bg-[#1B1B1B] text-white rounded-none"
                                            : "bg-[#54547A] hover:bg-[#1B1B1B] text-white rounded-none"
                                    }
                                    data-testid="countdown-toggle-button"
                                >
                                    {settings.countdown_enabled
                                        ? "Disable countdown"
                                        : "Enable countdown"}
                                </Button>
                            </div>
                        </div>

                        {webhookInfo && (
                            <div
                                className="bg-white border p-6 max-w-2xl mt-6"
                                data-testid="webhook-url-card"
                            >
                                <h3 className="font-poppins font-bold text-lg">
                                    JotForm Webhook URL
                                </h3>
                                <p className="font-sofia text-sm text-[#1B1B1B]/70 mt-1">
                                    Paste this exact URL into JotForm →
                                    Settings → Integrations → Webhooks. Every
                                    new submission will land as a Draft in the
                                    Submissions tab.
                                </p>
                                <div className="mt-3 flex items-stretch gap-2">
                                    <code
                                        className="flex-1 bg-[#FDF9EF] border border-black/10 px-3 py-2 font-mono text-xs break-all"
                                        data-testid="webhook-url-value"
                                    >
                                        {(typeof window !== "undefined"
                                            ? window.location.origin
                                            : "") + webhookInfo.path}
                                    </code>
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                (typeof window !== "undefined"
                                                    ? window.location.origin
                                                    : "") + webhookInfo.path,
                                            );
                                            toast.success("Copied.");
                                        }}
                                        className="rounded-none bg-[#1B1B1B] hover:bg-[#E63B26] text-white"
                                        data-testid="webhook-url-copy"
                                    >
                                        <Copy className="h-3 w-3 mr-1" /> Copy
                                    </Button>
                                </div>
                                <p className="font-sofia text-xs text-[#1B1B1B]/60 mt-2">
                                    Status:{" "}
                                    {webhookInfo.secret_set
                                        ? "Secret configured ✓"
                                        : "⚠ No secret — webhook will reject all requests until JOTFORM_WEBHOOK_SECRET is set in backend/.env"}
                                </p>
                            </div>
                        )}

                        <div
                            className="bg-white border p-6 max-w-2xl mt-6"
                            data-testid="spam-log-card"
                        >
                            <h3 className="font-poppins font-bold text-lg">
                                Spam Phrase Hits
                            </h3>
                            <p className="font-sofia text-sm text-[#1B1B1B]/70 mt-1">
                                Live count of every blacklist phrase that has
                                triggered an auto-ban. After ~30 days, prune
                                low-firing phrases from{" "}
                                <code className="font-mono text-xs">
                                    backend/blacklist.py
                                </code>{" "}
                                to cut false-positive risk.
                            </p>
                            {spamLog.length === 0 ? (
                                <p className="font-sofia text-sm text-[#1B1B1B]/50 italic mt-3">
                                    No hits yet. The blacklist is armed but no
                                    spam has come through.
                                </p>
                            ) : (
                                <table className="w-full mt-3 text-sm font-sofia">
                                    <thead className="border-b border-black/15">
                                        <tr>
                                            <th className="text-left font-poppins font-semibold uppercase tracking-[0.12em] text-xs py-2">
                                                Phrase
                                            </th>
                                            <th className="text-right font-poppins font-semibold uppercase tracking-[0.12em] text-xs py-2">
                                                Hits
                                            </th>
                                            <th className="text-right font-poppins font-semibold uppercase tracking-[0.12em] text-xs py-2">
                                                Last seen
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {spamLog.map((row) => (
                                            <tr
                                                key={row.phrase}
                                                className="border-b border-black/5"
                                                data-testid={`spam-log-row-${row.phrase.replace(/\s+/g, "-")}`}
                                            >
                                                <td className="py-2 font-mono text-xs">
                                                    {row.phrase}
                                                </td>
                                                <td className="py-2 text-right font-poppins font-semibold">
                                                    {row.count}
                                                </td>
                                                <td className="py-2 text-right text-[#1B1B1B]/60 text-xs">
                                                    {row.last_seen
                                                        ? new Date(
                                                              row.last_seen,
                                                          ).toLocaleString(
                                                              undefined,
                                                              {
                                                                  month: "short",
                                                                  day: "numeric",
                                                                  hour: "2-digit",
                                                                  minute: "2-digit",
                                                              },
                                                          )
                                                        : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
